import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { ListCardsSkeleton } from "../design/ListStates";
import {
  PROPERTY_OPTION_STATUSES,
  type AppRole,
  type PropertyOption,
  type PropertyOptionOwner,
  type Sale,
} from "../types";
import { usePropertyTypes } from "./InventoryFilters";
import { Field, OpcionSpecFields, SELECT_CLASS } from "./OpcionFormParts";
import {
  AMK_CHOICE,
  buildOpcionPayload,
  buildOwnerPayload,
  canSeeOwner,
  getResponsableChoices,
  toOpcionErrorMessage,
  toOpcionFormValues,
  validateOpcionForm,
  type OpcionFormErrors,
  type OpcionFormValues,
  type ResponsableChoice,
} from "./opcionRules";

type OpcionFormProps = {
  option?: PropertyOption;
  owner?: PropertyOptionOwner;
  role: AppRole;
  userId: Sale["id"] | undefined;
  choices: ResponsableChoice[];
  onSaved: (id: PropertyOption["id"]) => void;
  cancelTo: string;
};

/**
 * Create/edit form of an option. The owner contact block is rendered (and
 * saved) only when the viewer may see the owner of the chosen responsable;
 * otherwise those fields are absent from the page and from the payload.
 */
export const OpcionForm = ({
  option,
  owner,
  role,
  userId,
  choices,
  onSaved,
  cancelTo,
}: OpcionFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const only = choices.length === 1 && choices[0].id !== AMK_CHOICE;
  const [values, setValues] = useState<OpcionFormValues>(
    toOpcionFormValues(option, owner, only ? choices[0].id : ""),
  );
  const [errors, setErrors] = useState<OpcionFormErrors>({});
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const idPrefix = `opcion-${option?.id ?? "new"}`;
  // Archived types are not offered, except the one the option already has.
  const currentType = useMemo(
    () => (option ? [option.property_type] : []),
    [option],
  );
  const { choices: typeChoices } = usePropertyTypes(currentType);

  const chosen = buildOpcionPayload(values);
  const showOwner = canSeeOwner(role, userId, chosen);

  const set = (key: keyof OpcionFormValues, value: string) =>
    setValues({ ...values, [key]: value });

  const saveOwner = async (optionId: PropertyOption["id"]) => {
    const payload = buildOwnerPayload(values);
    if (!payload) return;
    if (owner) {
      await dataProvider.update("property_option_owners", {
        id: owner.id,
        data: payload,
        previousData: owner,
      });
    } else {
      await dataProvider.create("property_option_owners", {
        data: { ...payload, option_id: optionId },
      });
    }
  };

  const handleSave = async () => {
    const found = validateOpcionForm(values, showOwner);
    setErrors(found);
    setSaveError(undefined);
    if (Object.keys(found).length > 0) return;
    setIsSaving(true);
    try {
      const data = buildOpcionPayload(values);
      let optionId = option?.id;
      if (option) {
        await dataProvider.update("property_options", {
          id: option.id,
          data,
          previousData: option,
        });
      } else {
        const created = await dataProvider.create<PropertyOption>(
          "property_options",
          { data },
        );
        optionId = created.data.id;
      }
      if (showOwner && optionId != null) await saveOwner(optionId);
      notify(
        option ? "crm.opciones.updated_notice" : "crm.opciones.created_notice",
      );
      onSaved(optionId as PropertyOption["id"]);
    } catch (error) {
      setSaveError(toOpcionErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleSave();
      }}
    >
      <Field
        id={`${idPrefix}-address`}
        label={translate("resources.property_options.fields.address")}
        error={errors.address}
      >
        <Input
          id={`${idPrefix}-address`}
          value={values.address}
          disabled={isSaving}
          aria-invalid={errors.address != null}
          onChange={(event) => set("address", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-zone`}
        label={translate("resources.property_options.fields.zone")}
      >
        <Input
          id={`${idPrefix}-zone`}
          value={values.zone}
          disabled={isSaving}
          onChange={(event) => set("zone", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-type`}
        label={translate("resources.property_options.fields.property_type")}
        error={errors.property_type}
      >
        <select
          id={`${idPrefix}-type`}
          className={SELECT_CLASS}
          value={values.property_type}
          disabled={isSaving}
          onChange={(event) => set("property_type", event.target.value)}
        >
          <option value="">{translate("crm.opciones.choose_type")}</option>
          {typeChoices.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </Field>
      <OpcionSpecFields
        idPrefix={idPrefix}
        values={values}
        errors={errors}
        disabled={isSaving}
        set={set}
      />
      <Field
        id={`${idPrefix}-price`}
        label={translate("resources.property_options.fields.price")}
        error={errors.price}
      >
        <Input
          id={`${idPrefix}-price`}
          type="number"
          min={0}
          inputMode="decimal"
          className="tabular-nums"
          value={values.price}
          disabled={isSaving}
          aria-invalid={errors.price != null}
          onChange={(event) => set("price", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-status`}
        label={translate("resources.property_options.fields.status")}
      >
        <select
          id={`${idPrefix}-status`}
          className={SELECT_CLASS}
          value={values.status}
          disabled={isSaving}
          onChange={(event) => set("status", event.target.value)}
        >
          {PROPERTY_OPTION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {translate(`crm.opciones.status.${status}`)}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${idPrefix}-responsable`}
        label={translate("resources.property_options.fields.responsable")}
        error={errors.responsable}
      >
        <select
          id={`${idPrefix}-responsable`}
          className={SELECT_CLASS}
          value={values.responsable}
          disabled={isSaving || only}
          onChange={(event) => set("responsable", event.target.value)}
        >
          {only ? null : (
            <option value="">
              {translate("crm.opciones.choose_responsable")}
            </option>
          )}
          {choices.map((choice) => (
            <option key={choice.id} value={choice.id}>
              {choice.name}
            </option>
          ))}
        </select>
      </Field>

      {showOwner ? (
        <fieldset
          data-testid="owner-block"
          className="flex flex-col gap-4 rounded-md border p-4"
        >
          <legend className="px-1 font-semibold">
            {translate("crm.opciones.owner_title")}
          </legend>
          <p className="text-sm text-muted-foreground">
            {translate("crm.opciones.owner_form_hint")}
          </p>
          <Field
            id={`${idPrefix}-owner-name`}
            label={translate(
              "resources.property_option_owners.fields.full_name",
            )}
            error={errors.owner_full_name}
          >
            <Input
              id={`${idPrefix}-owner-name`}
              value={values.owner_full_name}
              disabled={isSaving}
              aria-invalid={errors.owner_full_name != null}
              onChange={(event) => set("owner_full_name", event.target.value)}
            />
          </Field>
          <Field
            id={`${idPrefix}-owner-phone`}
            label={translate("resources.property_option_owners.fields.phone")}
          >
            <Input
              id={`${idPrefix}-owner-phone`}
              type="tel"
              value={values.owner_phone}
              disabled={isSaving}
              onChange={(event) => set("owner_phone", event.target.value)}
            />
          </Field>
          <Field
            id={`${idPrefix}-owner-email`}
            label={translate("resources.property_option_owners.fields.email")}
            error={errors.owner_email}
          >
            <Input
              id={`${idPrefix}-owner-email`}
              type="email"
              value={values.owner_email}
              disabled={isSaving}
              aria-invalid={errors.owner_email != null}
              onChange={(event) => set("owner_email", event.target.value)}
            />
          </Field>
          <Field
            id={`${idPrefix}-owner-notes`}
            label={translate("resources.property_option_owners.fields.notes")}
          >
            <Textarea
              id={`${idPrefix}-owner-notes`}
              value={values.owner_notes}
              disabled={isSaving}
              onChange={(event) => set("owner_notes", event.target.value)}
            />
          </Field>
        </fieldset>
      ) : null}

      {saveError ? (
        <p role="alert" className="text-sm text-destructive">
          {saveError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button asChild variant="ghost" className="min-h-11">
          <Link to={cancelTo}>{translate("ra.action.cancel")}</Link>
        </Button>
        <Button
          type="submit"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </form>
  );
};

/** Identity + responsable choices shared by the create and edit pages. */
const useOpcionFormContext = () => {
  const { identity } = useGetIdentity();
  const { data: sales, isPending } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "advisor_code", order: "ASC" },
  });
  if (!identity || isPending) return null;
  const role = identity.role as AppRole;
  return {
    role,
    userId: identity.id,
    choices: getResponsableChoices(role, identity.id, sales ?? []),
  };
};

const FormPage = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="mx-auto max-w-2xl space-y-4 p-4 pb-8">
    <h1 className="text-2xl font-semibold">{title}</h1>
    <Card>
      <CardContent>{children}</CardContent>
    </Card>
  </div>
);

/** Create page: an asesora can only pick herself as responsable. */
export const OpcionCreate = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const context = useOpcionFormContext();
  return (
    <FormPage title={translate("crm.opciones.create_title")}>
      {context ? (
        <OpcionForm
          {...context}
          cancelTo="/property_options"
          onSaved={(id) => navigate(`/property_options/${id}/show`)}
        />
      ) : (
        <ListCardsSkeleton rows={3} />
      )}
    </FormPage>
  );
};

/** Edit page: the owner is requested only if the viewer may see it. */
export const OpcionEdit = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { id } = useParams();
  const context = useOpcionFormContext();
  const { data: option } = useGetOne<PropertyOption>(
    "property_options",
    { id: id as string },
    { enabled: id != null },
  );
  const mayReadOwner =
    context != null &&
    option != null &&
    canSeeOwner(context.role, context.userId, option);
  const { data: owners, isPending: isOwnerPending } =
    useGetList<PropertyOptionOwner>(
      "property_option_owners",
      {
        filter: { option_id: id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      },
      { enabled: mayReadOwner },
    );
  const title = translate("crm.opciones.edit_title");
  if (!context || !option || (mayReadOwner && isOwnerPending)) {
    return (
      <FormPage title={title}>
        <ListCardsSkeleton rows={3} />
      </FormPage>
    );
  }
  return (
    <FormPage title={title}>
      <OpcionForm
        {...context}
        option={option}
        owner={owners?.[0]}
        cancelTo={`/property_options/${option.id}/show`}
        onSaved={() => navigate(`/property_options/${option.id}/show`)}
      />
    </FormPage>
  );
};
